import type {
  TrustComplaint,
  TrustComplaintCounts,
  TrustComplaintQueue,
  TrustComplaintStatus,
} from "@/api/trust_complaints";
import { TRUST_COMPLAINT_STATUS_LABELS } from "@/api/trust_complaints";

const SOURCE_LABELS: Record<string, string> = {
  ordinal_connect_widget: "Ordinal Connect",
  user: "Staff",
};

export function trustComplaintSourceLabel(source: string | null | undefined): string {
  if (!source) return "Unknown source";
  return SOURCE_LABELS[source] ?? source.replace(/_/g, " ");
}

export function trustComplaintSourcePillClass(source: string | null | undefined): string {
  switch (source) {
    case "ordinal_connect_widget":
      return "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300";
    case "user":
      return "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function trustComplaintStatusLabel(status: TrustComplaintStatus): string {
  return TRUST_COMPLAINT_STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}

export function trustComplaintStatusPillClass(status: TrustComplaintStatus): string {
  switch (status) {
    case "new":
      return "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300";
    case "reviewed":
      return "bg-indigo-100 text-indigo-900 dark:bg-indigo-950 dark:text-indigo-300";
    case "in_progress":
      return "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300";
    case "closed":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function trustComplaintIsOpen(complaint: TrustComplaint): boolean {
  return complaint.status !== "closed";
}

export function trustComplaintQueueFor(complaint: TrustComplaint): TrustComplaintQueue {
  return trustComplaintIsOpen(complaint) ? "open" : "closed";
}

export const EMPTY_TRUST_COMPLAINT_COUNTS: TrustComplaintCounts = {
  open: 0,
  closed: 0,
  all: 0,
  by_status: { new: 0, reviewed: 0, in_progress: 0, closed: 0 },
};

export function trustComplaintDetailTitle(complaint: TrustComplaint): string {
  const text = complaint.complaint.trim();
  if (text.length <= 100) return text;
  return `${text.slice(0, 97)}…`;
}

export function trustComplaintListTitle(complaint: TrustComplaint): string {
  const text = complaint.complaint.trim();
  if (text.length <= 80) return text;
  return `${text.slice(0, 77)}…`;
}

export function trustComplaintListMeta(complaint: TrustComplaint): string {
  const parts: string[] = [];
  if (complaint.contact_name) parts.push(complaint.contact_name);
  else if (complaint.contact_email) parts.push(complaint.contact_email);
  if (complaint.photo_count > 0) {
    parts.push(`${complaint.photo_count} photo${complaint.photo_count === 1 ? "" : "s"}`);
  }
  return parts.join(" · ");
}

export function trustComplaintSummaryStats(complaints: TrustComplaint[]) {
  const withContact = complaints.filter(
    (complaint) => complaint.contact_name || complaint.contact_email,
  ).length;
  const withPhotos = complaints.filter((complaint) => complaint.photo_count > 0).length;

  return {
    total: complaints.length,
    withContact,
    withPhotos,
  };
}

export function trustComplaintAddressLine(
  addressData: TrustComplaint["address_data"],
): string | null {
  if (!addressData) return null;
  const formatted = addressData.formatted_address?.toString().trim();
  if (formatted) return formatted;
  const address = addressData.address?.toString().trim();
  return address || null;
}

export const TRUST_COMPLAINT_SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "ordinal_connect_widget", label: "Ordinal Connect" },
  { value: "user", label: "Staff" },
] as const;

export type TrustComplaintSourceFilter = (typeof TRUST_COMPLAINT_SOURCE_OPTIONS)[number]["value"];
